import { Alert, Button, Col, Modal, Row, Spin, Typography, App } from 'antd';
import { Form } from 'antd';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { container } from 'src/inversify.config';
import {
  CreateRoleAssignmentItem,
  RoleAssignmentItemStore,
  RoleAssignmentSummary,
  RoleAssignmentSummaryStore,
} from 'src/stores/role-assignments-store';

import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import RoleSelect from 'src/common/components/business/role-select';
import { IdentityType, PermissionsConstants, Selector } from 'src/common/types';
import NewItemModal from 'src/core/ui/newitemmodal';
import RoleAssignmentAttributesComponent from 'src/pages/roles/role-attributes/role-attributes';
import { useCurrentUserPermissionsFilter } from '../../../common/permission-hooks';
import { getCurrentOrg } from '../../../common/utils';
import { useIdentity } from '../../../core/services/authentication';
import { Query } from '../../../core/stores/data-store';
import {
  getUnlimitedLicenseAttributes,
  LicenseCreationAttributes,
  LicenseCreationAttributesStore,
  LicenseManagerAssignmentAttributes,
  NewLicensCreationAttributes,
} from '../../../stores/licensing-store';
import { licenseManagerRoleName, licenseReviewerRoleName, StructmasterUserRoleName } from '../../../stores/roles-store';
import LicenseAttributesComponent from '../../licenses/license-attributes/license-attributes';
import { startCase } from 'lodash';
import { useCheckGlobalLicensesPermission } from 'src/common/hooks/use-check-global-licenses-permission';
import { theme } from 'antd';
const { useToken } = theme;

interface UserRoleEditFormData {
  roles: { value: string; label: string }[];
}
const { Text } = Typography;
const perm = [PermissionsConstants.ManageGlobalLicenses, PermissionsConstants.ManageLicenses];
const OrganizationMemberRoleEdit: FC<{
  identityId: string;
  organizationId: string;
  onClose: (changed: boolean) => void;
  selectedRoles: { value: string; label: string; expirationDate?: Date }[];
  isPartner: boolean;
  lockedRoleNames: string[];
  excludeFromDeleteRoleNames: string[];
}> = ({ onClose, identityId, organizationId, selectedRoles, isPartner, lockedRoleNames, excludeFromDeleteRoleNames }) => {
  const identity = useIdentity();
  const { t } = useTranslation();
  const newRoleAssignmentStore = container.get(RoleAssignmentItemStore);
  const newRoleAssignmentState = newRoleAssignmentStore.state;
  const licenseCreationAttributesStore = useMemo(() => container.get(LicenseCreationAttributesStore), []);
  const licenseCreationAttributesState = licenseCreationAttributesStore.state;
  const roleAssignmentSummaryStore = container.get(RoleAssignmentSummaryStore);
  const roleAssignmentSummaryState = roleAssignmentSummaryStore.state;
  const [userPermissions, checkingPermissions] = useCurrentUserPermissionsFilter(identity, getCurrentOrg(identity).id, perm);
  const [hasGlobalLicenseManagerPermission] = useCheckGlobalLicensesPermission();
  const hasLicenseManagerPermission =
    !checkingPermissions && (userPermissions.has(PermissionsConstants.ManageLicenses) || hasGlobalLicenseManagerPermission);

  const [saveRoleWarning, setSaveRoleWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [licenseAttributesVisible, setLicenseAttributesVisible] = useState<boolean>(false);
  const [licenseAttributes, setLicenseAttributes] = useState<LicenseManagerAssignmentAttributes>();
  const [customError, setCustomError] = useState('');

  const [initialAttr, setinitialAttr] = useState<LicenseCreationAttributes>();
  const [loadingLicenseAttributes, setLoadingLicenseAttributes] = useState(true);
  const [dataChanged, setDataChanged] = useState(false);
  const [licenseAttributesChanged, setLicenseAttributesChanged] = useState(false);

  const rolesToAdd = useRef<{ value: string; label: string; expirationDate?: Date }[]>([]);
  const rolesToRemove = useRef<RoleAssignmentSummary[]>([]);
  const rolesToUpdate = useRef<{ value: string; label: string; expirationDate?: Date }[]>([]);
  const licenseAssignmentId = useRef<string>();
  const [licenseAttributesErrors, setLicenseAttributesErrors] = useState(false);
  const unlimitedLicenseDefaults = getUnlimitedLicenseAttributes();
  const currentRoles = useRef<{ value: string; label: string; expirationDate?: Date }[]>(selectedRoles);
  const originalRoles = useRef<{ value: string; label: string; expirationDate?: Date }[]>(selectedRoles);
  const licenseAttributesEditable = !lockedRoleNames.includes(licenseManagerRoleName);
  const [noRolesSelected, setNoRolesSelected] = useState(false);
  const { modal } = App.useApp();

  const [form] = Form.useForm();
  const { token } = useToken();

  // console.log(selectedRoles);

  const query = {
    filter: {
      identityId: { value: identityId, type: 'guid' },
      identityType: IdentityType.User,
      scopeId: { value: organizationId, type: 'guid' },
    },
    skip: 0,
    take: 10000,
  } as Query;

  useEffect(() => {
    onRolesChange(selectedRoles);
    licenseCreationAttributesStore.getByUserScope({ scopeId: organizationId, identityId: identityId, identityType: IdentityType.User }).then(() => {
      roleAssignmentSummaryStore.load(query).then(() => {
        setinitialAttr(licenseCreationAttributesState.item.value as LicenseCreationAttributes);
        const roleAssignment = roleAssignmentSummaryState.items.value.filter((x) => x.role === licenseManagerRoleName).firstOrDefault();
        licenseAssignmentId.current = roleAssignment?.id;
        setLoadingLicenseAttributes(false);
      });
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getWarningMessage = async () => {
    let item = (await form.validateFields()) as UserRoleEditFormData;

    const uniqueValues = currentRoles.current.filter((v, i, a) => {
      return a.findIndex((t) => t.value === v.value) === i;
    });

    if (uniqueValues.length !== currentRoles.current.length) {
      modal.error({ title: t('DuplicatedRolesWarning') });
      return;
    }

    if (item) {
      var rolesAdd = rolesToAdd.current.map((x) => x.label);
      var rolesAddString = rolesAdd.length > 0 ? rolesAdd[0] : '';

      for (let i = 1; i < rolesAdd.length; i++) {
        rolesAddString = rolesAddString.concat(', ' + rolesAdd[i]);
      }

      var rolesRemove = rolesToRemove.current.map((x) => x.role);
      var rolesRemoveString = rolesRemove.length > 0 ? rolesRemove[0] : '';

      var rolesUpdate = rolesToUpdate.current.map((x) => x.label);
      var rolesUpdateString = rolesUpdate.length > 0 ? rolesUpdate[0] : '';

      for (let i = 1; i < rolesRemove.length; i++) {
        rolesRemoveString = rolesRemoveString.concat(', ' + rolesRemove[i]);
      }

      let fullMessage = '';

      if (rolesAddString) {
        fullMessage = fullMessage.concat(t('RolesWillBeAssigned').replace('{0}', rolesAddString));
      }
      if (rolesRemoveString) {
        fullMessage = fullMessage.concat(t('RolesWillBeRemoved').replace('{0}', rolesRemoveString));
      }
      if (rolesUpdateString) {
        fullMessage = fullMessage.concat(t('RolesWillBeUpdated').replace('{0}', rolesUpdateString));
      }
      if (licenseAttributes) {
        fullMessage = fullMessage.concat(t('LicenseAttributesWillBeUpdated'));
      }

      setWarningMessage(fullMessage);
    }
    setSaveRoleWarning(true);
  };

  const getCurrentLicenseAttributes = () => {
    if (licenseAttributes) {
      var item: NewLicensCreationAttributes = {
        identityType: IdentityType.User,
        canCreateRecurrentLicenses: licenseAttributes.canCreateRecurrentLicenses ?? false,
        maximumLicenseDueDate: licenseAttributes.maximumLicenseDueDate,
        maxLicensesPeriodInDays: licenseAttributes.maxLicensesPeriodInDays,
        maxNumberOfLicensesPerMonth: licenseAttributes.maxNumberOfLicensesPerMonth,
        allowedDevices: licenseAttributes.allowedDevices,
        identityId: identityId,
        scopeId: organizationId,
        needsSignOff: licenseAttributes.needsSignOff,
        canCreateUnlimitedLicense: licenseAttributes.canCreateUnlimitedLicense,
      };
      if (item.canCreateUnlimitedLicense) {
        item.maximumLicenseDueDate = unlimitedLicenseDefaults.untilDate;
        item.maxLicensesPeriodInDays = unlimitedLicenseDefaults.maxPeriodInDays;
      }
      return item;
    }
    return null;
  };

  const createLicenseAttributes = async (updateDate = false) => {
    const item = getCurrentLicenseAttributes();
    if (item) {
      await licenseCreationAttributesStore.post(item);
    }
  };

  const updateLicenseAttributes = async () => {
    const item = getCurrentLicenseAttributes();
    if (item && initialAttr && licenseAttributes && licenseAssignmentId.current) {
      const toSave: LicenseCreationAttributes = {
        id: initialAttr.id,
        maxNumberOfLicensesInCurrentMonth: initialAttr.maxNumberOfLicensesInCurrentMonth,
        ...item,
      };
      await licenseCreationAttributesStore.save(initialAttr.id, toSave);
    }
  };

  const onRolesChange = async (values: { value: string; label: string; expirationDate?: Date }[]) => {
    currentRoles.current = values;
    setNoRolesSelected(currentRoles.current.length === 0);
    var licenseMnagerRoleSelected = values.any((x) => x.label === licenseManagerRoleName);
    if (!licenseAttributesVisible) {
      licenseAttributesEditable && licenseMnagerRoleSelected && setLicenseAttributesVisible(true);
    } else {
      licenseAttributesEditable && !licenseMnagerRoleSelected && setLicenseAttributesVisible(false);
    }

    const selectedRolesId = values.map((x) => x.value);
    const originalRolesId = selectedRoles.map((x) => x.value);

    roleAssignmentSummaryStore.load(query).then(() => {
      rolesToRemove.current = roleAssignmentSummaryState.items.value.filter((x) => {
        let ignoreRoleForRemove = (x.role === licenseManagerRoleName || x.role === licenseReviewerRoleName) && !hasGlobalLicenseManagerPermission;
        ignoreRoleForRemove = ignoreRoleForRemove || (x.role === StructmasterUserRoleName && !hasLicenseManagerPermission);
        return !selectedRolesId.includes(x.roleId) && !ignoreRoleForRemove;
      });
      rolesToAdd.current = values.filter((x) => !originalRolesId.includes(x.value));
      rolesToUpdate.current = values.filter(
        (x) => originalRolesId.includes(x.value) && x.expirationDate !== selectedRoles.find((v) => v.value === x.value)?.expirationDate
      );

      setDataChanged(rolesToRemove.current.length !== 0 || rolesToAdd.current.length !== 0 || rolesToUpdate.current.length > 0);
    });
  };

  const onLicenseAttributesChange = (item: LicenseManagerAssignmentAttributes) => {
    setLicenseAttributesChanged(true);
    setDataChanged(rolesToRemove.current.length !== 0 || rolesToAdd.current.length !== 0 || rolesToUpdate.current.length > 0);
    setLicenseAttributes(item);
  };

  const removeRoles = async () => {
    rolesToRemove.current.forEach(async (value) => {
      await roleAssignmentSummaryStore.delete(value.id);
      if (value.role === licenseManagerRoleName) {
        await licenseCreationAttributesStore.deleteByUserScope({ identityId: identityId, scopeId: organizationId, identityType: IdentityType.User });
      }
    });
  };

  const updateRoles = async () => {
    rolesToUpdate.current.forEach(async (value) => {
      const assignmentId = roleAssignmentSummaryState.items.value.find((v) => v.roleId === value.value)?.id;
      if (assignmentId) {
        await roleAssignmentSummaryStore.updateExpiration(assignmentId, { newExpirationDate: value.expirationDate!, scopeId: organizationId });
      }
    });
  };

  const addNewRoles = async () => {
    const postData: CreateRoleAssignmentItem = {
      identityId: identityId,
      identityType: IdentityType.User,
      roles: rolesToAdd.current.map((r) => ({ role: r.label, roleId: r.value, expirationDate: r.expirationDate })),
      scopeId: organizationId,
    };

    let response = await newRoleAssignmentStore.create(postData);

    if (response?.isSuccess && rolesToAdd.current.map((x) => x.label).includes(licenseManagerRoleName)) {
      createLicenseAttributes();
    }
  };

  const rolesUnchanged = () =>
    rolesToAdd.current.length === 0 && rolesToRemove.current.length === 0 && rolesToUpdate.current.length === 0 && !licenseAttributes;

  const handleEditRoles = async () => {
    if (rolesUnchanged()) {
      handleClose(false);
      return;
    }

    if (rolesToAdd.current.length > 0) await addNewRoles();
    if (rolesToRemove.current.length > 0) await removeRoles();
    if (rolesToUpdate.current.length > 0) await updateRoles();

    const creatingNewLicenseAttributes = selectedRoles.map((x) => x.label).includes(licenseManagerRoleName) && !initialAttr && licenseAttributes;
    if (creatingNewLicenseAttributes) {
      await createLicenseAttributes(true);
      return;
    }

    if (!rolesToAdd.current.map((x) => x.label).includes(licenseManagerRoleName) && licenseAttributes) await updateLicenseAttributes();
  };

  const handleClose = (response?: any) => {
    newRoleAssignmentStore.clearMessages();
    onClose(response);
  };

  const roleIsDisabledFromSelect = (roleName: string) => {
    let roleIsLocked = excludeFromDeleteRoleNames.some((v) => startCase(roleName.toLowerCase()) === startCase(v.toLowerCase()));
    roleIsLocked = roleIsLocked && originalRoles.current.some((v) => startCase(roleName.toLowerCase()) === startCase(v.label.toLowerCase()));
    return roleIsLocked;
  };

  const disableRemoveRole = (listId: number) => {
    const roleName = form.getFieldValue(['roles', listId, 'role'])?.label;
    if (!roleName) return false;
    let roleIsLocked = excludeFromDeleteRoleNames.some((v) => startCase(roleName.toLowerCase()) === startCase(v.toLowerCase()));
    roleIsLocked = roleIsLocked && originalRoles.current.some((v) => startCase(roleName.toLowerCase()) === startCase(v.label.toLowerCase()));
    return roleIsLocked;
  };

  const disableEditRole = (listId: number) => {
    const roleName = form.getFieldValue(['roles', listId, 'role'])?.label;
    if (!roleName) return false;
    let roleIsLocked = lockedRoleNames.some((v) => startCase(roleName.toLowerCase()) === startCase(v.toLowerCase()));
    roleIsLocked = roleIsLocked && originalRoles.current.some((v) => startCase(roleName.toLowerCase()) === startCase(v.label.toLowerCase()));
    return roleIsLocked;
  };

  return (
    <NewItemModal
      width={600}
      className="figma-modal"
      title={t('EditRolesMessage')}
      onNewItem={getWarningMessage}
      onClose={() => {
        handleClose(false);
      }}
      onOkButtonIsDisabled={
        newRoleAssignmentState.isBusy.get() ||
        licenseCreationAttributesState.isBusy.get() ||
        (!dataChanged && !licenseAttributesChanged) ||
        licenseAttributesErrors ||
        noRolesSelected
      }
      onCancelButtonIsDisabled={newRoleAssignmentState.isBusy.get() || licenseCreationAttributesState.isBusy.get()}
      closable={!newRoleAssignmentState.isBusy.get()}
      maskClosable={!newRoleAssignmentState.isBusy.get()}
      okText={t('Confirm')}
      errorMessage={newRoleAssignmentState.errorMessage}
      body={
        <>
          {(customError || '').length !== 0 && <Alert type="error" message={customError} closable onClose={() => setCustomError('')} />}
          <Spin spinning={newRoleAssignmentState.isBusy.get() || checkingPermissions === true || licenseCreationAttributesState.isBusy.get()}>
            <Form
              style={{ width: '100%' }}
              size="middle"
              layout="vertical"
              initialValues={{ roles: selectedRoles.map((r) => ({ role: { label: r.label, value: r.value }, expirationDate: r.expirationDate })) }}
              form={form}
              name="dynamic_form_nest_item"
              autoComplete="off"
            >
              {noRolesSelected && <Text type="danger">{t('UserHasNoRoleWarning')}</Text>}
              <Form.List name="roles">
                {(fields, { add, remove }) => (
                  <>
                    <div style={{ maxHeight: '40vh', overflowY: 'auto', overflowX: 'clip' }}>
                      {fields.map(({ key, name, ...restField }) => (
                        <Row gutter={[10, 10]} key={key}>
                          <Col span={15}>
                            <Form.Item
                              {...restField}
                              name={[name, 'role']}
                              rules={[
                                { required: true, message: t('PleaseSelectRole') },
                                (formInstance) => ({
                                  message: t('DuplicatedRolesWarning'),
                                  validator(_, formItem) {
                                    const items: { role: { label: string; value: string } }[] = formInstance.getFieldValue('roles');

                                    if (formItem === undefined) return Promise.resolve();

                                    const duplicatedRoles = items.filter((item) => item && item.role.value === formItem.value).length > 1;

                                    if (duplicatedRoles) {
                                      return Promise.reject();
                                    } else {
                                      return Promise.resolve();
                                    }
                                  },
                                }),
                              ]}
                            >
                              <RoleSelect
                                disabled={disableRemoveRole(name) || disableEditRole(name)}
                                allowClear={false}
                                editRoles={true}
                                organizationId={organizationId}
                                value={!dataChanged ? selectedRoles[name] : undefined}
                                onChange={(item) => {
                                  setDataChanged(true);
                                  form.setFieldValue(['roles', name, 'role'], item);
                                  const data = form.getFieldsValue();
                                  onRolesChange(
                                    data.roles.map((v: any) => ({ value: v?.role?.value, label: v?.role?.label, expirationDate: v?.expirationDate }))
                                  );
                                }}
                                maxSelector={isPartner ? Selector.Partners : Selector.Organization}
                                excludeNames={[
                                  !hasGlobalLicenseManagerPermission || roleIsDisabledFromSelect(licenseManagerRoleName)
                                    ? licenseManagerRoleName
                                    : '',
                                  !hasGlobalLicenseManagerPermission || roleIsDisabledFromSelect(licenseReviewerRoleName)
                                    ? licenseReviewerRoleName
                                    : '',
                                  !hasLicenseManagerPermission || roleIsDisabledFromSelect(StructmasterUserRoleName) ? StructmasterUserRoleName : '',
                                ]}
                              />{' '}
                            </Form.Item>
                          </Col>
                          <Col span={7}>
                            <Form.Item
                              {...restField}
                              name={[name, 'expirationDate']}
                              rules={[{ required: true, message: t('PleaseEnterExpirationDate') }]}
                            >
                              <RoleAssignmentAttributesComponent
                                disabled={disableEditRole(name)}
                                usingForm={false}
                                showLabel={false}
                                initialValue={!dataChanged ? selectedRoles[name].expirationDate : undefined}
                                onChange={(v) => {
                                  form.setFieldValue(['roles', name, 'expirationDate'], v.expirationDate);
                                  const data = form.getFieldsValue();
                                  onRolesChange(
                                    data.roles.map((v: any) => ({
                                      value: v?.role?.value,
                                      label: v?.role?.label,
                                      expirationDate: v?.expirationDate,
                                    }))
                                  );
                                  setDataChanged(true);
                                }}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={2}>
                            <MinusCircleOutlined
                              style={{ color: disableRemoveRole(name) || disableEditRole(name) ? token.colorTextDisabled : token.colorText }}
                              onClick={
                                disableRemoveRole(name) || disableEditRole(name)
                                  ? undefined
                                  : () => {
                                      remove(name);
                                      const data = form.getFieldsValue();
                                      onRolesChange(
                                        data.roles.map((v: any) => ({
                                          value: v?.role?.value,
                                          label: v?.role?.label,
                                          expirationDate: v?.expirationDate,
                                        }))
                                      );
                                      setDataChanged(true);
                                    }
                              }
                            />
                          </Col>
                        </Row>
                      ))}
                    </div>
                    <Form.Item>
                      <Button
                        type="dashed"
                        onClick={() => {
                          setDataChanged(true);
                          setNoRolesSelected(false);
                          add();
                        }}
                        block
                        icon={<PlusOutlined />}
                      >
                        {t('AddRole')}
                      </Button>
                    </Form.Item>
                  </>
                )}
              </Form.List>

              {licenseAttributesVisible && !loadingLicenseAttributes && (
                <LicenseAttributesComponent
                  onChange={onLicenseAttributesChange}
                  initialAttr={initialAttr}
                  onFormValidation={(errors) => setLicenseAttributesErrors(errors)}
                />
              )}
            </Form>
          </Spin>
          {saveRoleWarning && warningMessage && (
            <Modal
              title={<h3>{`${t('Confirmation')}`}</h3>}
              open={saveRoleWarning}
              onOk={async () => {
                setSaveRoleWarning(false);
                await handleEditRoles();
                await roleAssignmentSummaryStore.load(query);
                handleClose(true);
              }}
              onCancel={() => handleClose(false)}
              okText={t('OK')}
              okButtonProps={{
                style: { width: '45%', float: 'right' },
                disabled: licenseCreationAttributesState.isBusy.get() || newRoleAssignmentState.isBusy.get(),
              }}
              cancelText={t('Cancel')}
              cancelButtonProps={{
                type: 'default',
                style: { width: '45%' },
                disabled: licenseCreationAttributesState.isBusy.get() || newRoleAssignmentState.isBusy.get(),
              }}
            >
              <Spin spinning={newRoleAssignmentState.isBusy.get() || checkingPermissions === true || licenseCreationAttributesState.isBusy.get()}>
                <p>{warningMessage}</p>
                <p>{t('DoYouWantToContinue')}</p>
              </Spin>
            </Modal>
          )}
        </>
      }
    />
  );
};

export default OrganizationMemberRoleEdit;
